import React, { useState, useEffect, useRef } from 'react'

import cn from 'classnames'
import Icon from '../Icon2'

import './Tabs.css'

type tabInfoArray = {
    id: string
    title?: string
    componentTitle?: React.ReactNode
    componentData?: React.ReactNode
    data?: string
    callBack?: Function | undefined
    disabled?: boolean
    testId?: string,
    hasError?: boolean
}

export type tabProps = {
    tabInfo: Array<tabInfoArray>
    activeTab?: string | undefined
    type?: 'primary' | 'secondary' | 'tertiary'
    className?: string
    testId?: string
    onKeyDown?: (value: any) => void
    version?: 'v1' | 'v2'
    tabSize?: 'small' | 'regular'
    shouldHaveBorder?: Boolean
    enableCarousel?: boolean
    carouselContainerElementId?: string
}

type tabCreatorProps = {
    id: any
    tabHeader: string | undefined
    tabComponentHeader: React.ReactNode | undefined
    disabled?: boolean
    TabIndex: number
    type?: tabProps["type"]
}

type tabDataCreatorProps = {
    tabData: string | undefined
    tabComponent: React.ReactNode | undefined
}

const TabCreator = (props: tabCreatorProps) => {
    let displayHeader: any

    if (props.tabHeader !== undefined) {
        displayHeader = props.tabHeader
    } else if (props.tabComponentHeader !== undefined) {
        displayHeader = props.tabComponentHeader
    } else {
        displayHeader = null
    }
    return displayHeader === null ? null : displayHeader
}

const TabDataCreator = (props: tabDataCreatorProps) => {
    let displayData: any
    if (props.tabData !== undefined) {
        displayData = props.tabData
    } else if (props.tabComponent !== undefined) {
        displayData = props.tabComponent
    } else {
        displayData = null
    }
    return displayData === null ? null : <div>{displayData}</div>
}

const TabIndexSetter = (Tabs: Array<tabInfoArray>, activeTab: string | undefined) => {
    let index: any
    if (activeTab === undefined) {
        index = 0
    } else {
        Tabs.map((indiTabs: any) => {
            if (indiTabs.id === activeTab) {
                index = Tabs.indexOf(indiTabs)
            }
        })
    }
    return index
}

const Tabs = (props: tabProps) => {
    const { 
        type = "primary", 
        className, 
        testId = "cs-tabs", 
        tabInfo = [], 
        tabSize = "regular", 
        shouldHaveBorder = true,
        enableCarousel = false,
        carouselContainerElementId = "entryFormComponent-wrapper-id"
    } = props

    const [TabIndex, setTabIndex] = useState(TabIndexSetter(tabInfo, props.activeTab))
    const [prevActiveTab, setPrevActiveTab] = useState(props.activeTab)
    const TabInfo = tabInfo

    // Carousel refs and state
    const tabsContainerRef = useRef<HTMLDivElement>(null)
    const tabsWrapperRef = useRef<HTMLDivElement>(null)
    const [containerWidth, setContainerWidth] = useState(0)
    const [tabsWidth, setTabsWidth] = useState(0)
    const [scrollLeft, setScrollLeft] = useState(0)
    const [showPrevButton, setShowPrevButton] = useState(false)
    const [showNextButton, setShowNextButton] = useState(false)

    const TabType = `Tab--${type}`
    const TabItemType = `Tab__item--${type}`
    const classNames = cn('Tab', [TabType], className, {
        ['Tab--v2']: props.version === 'v2' && type === 'secondary',
        ['Tab--small']: props.version === 'v2' && tabSize === 'small',
        ['Tab--primary-v2']: props.version === 'v2' && type === 'primary',
        ['Tab--primary-v2--no-border']: shouldHaveBorder === false && props.version === 'v2' && type === 'primary'
    })

    useEffect(() => {
        if (props.activeTab !== undefined) {
            if (prevActiveTab !== props.activeTab) {
                setTabIndex(TabIndexSetter(TabInfo, props.activeTab))
                setPrevActiveTab(props.activeTab)
            }
        }
    }, [props.activeTab])

    // Carousel width calculation and button visibility
    useEffect(() => {
        if (!enableCarousel) return

        const calculateWidths = () => {
            const containerElement = document.getElementById(carouselContainerElementId)
            const tabsContainer = tabsContainerRef.current
            const tabsWrapper = tabsWrapperRef.current

            if (containerElement && tabsContainer && tabsWrapper) {
                const newContainerWidth = containerElement.offsetWidth
                const newTabsWidth = tabsWrapper.scrollWidth
                
                setContainerWidth(newContainerWidth)
                setTabsWidth(newTabsWidth)
                
                // Show/hide navigation buttons
                const needsCarousel = newTabsWidth > newContainerWidth
                
                // If carousel is no longer needed, reset scroll position
                if (!needsCarousel && scrollLeft > 0) {
                    setScrollLeft(0)
                    tabsWrapper.style.transform = 'translateX(0px)'
                }
                
                setShowPrevButton(needsCarousel && scrollLeft > 0)
                setShowNextButton(needsCarousel && scrollLeft < newTabsWidth - newContainerWidth)
            }
        }

        calculateWidths()
        
        // Set up ResizeObserver to monitor the specific container element
        let resizeObserver: ResizeObserver | null = null
        const containerElement = document.getElementById(carouselContainerElementId)
        
        if (containerElement && 'ResizeObserver' in window) {
            resizeObserver = new ResizeObserver(() => {
                requestAnimationFrame(calculateWidths)
            })
            resizeObserver.observe(containerElement)
        }
        
        // Fallback: window resize for browsers without ResizeObserver
        const handleResize = () => requestAnimationFrame(calculateWidths)
        window.addEventListener('resize', handleResize)
        
        return () => {
            if (resizeObserver) {
                resizeObserver.disconnect()
            }
            window.removeEventListener('resize', handleResize)
        }
    }, [enableCarousel, carouselContainerElementId, scrollLeft, tabInfo])

    // Update button visibility when scroll changes
    useEffect(() => {
        if (!enableCarousel) return
        
        const needsCarousel = tabsWidth > containerWidth
        setShowPrevButton(needsCarousel && scrollLeft > 0)
        setShowNextButton(needsCarousel && scrollLeft < tabsWidth - containerWidth)
    }, [enableCarousel, scrollLeft, tabsWidth, containerWidth])

    // Navigation handlers for carousel
    const handlePrevious = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        
        if (!enableCarousel || !tabsWrapperRef.current) return
        
        const scrollAmount = Math.min(200, scrollLeft) // Scroll by 200px or remaining scroll
        const newScrollLeft = Math.max(0, scrollLeft - scrollAmount)
        
        setScrollLeft(newScrollLeft)
        tabsWrapperRef.current.style.transform = `translateX(-${newScrollLeft}px)`
    }

    const handleNext = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        
        if (!enableCarousel || !tabsWrapperRef.current) return
        
        const maxScroll = tabsWidth - containerWidth
        const scrollAmount = Math.min(200, maxScroll - scrollLeft) // Scroll by 200px or remaining scroll
        const newScrollLeft = Math.min(maxScroll, scrollLeft + scrollAmount)
        
        setScrollLeft(newScrollLeft)
        tabsWrapperRef.current.style.transform = `translateX(-${newScrollLeft}px)`
    }

    const handleClick = (event: any) => {
        let id = event.currentTarget.id
        if (!props.activeTab) {
            setTabIndex(parseInt(event.currentTarget.id))
        }
        if (TabInfo[event.currentTarget.id]?.callBack) {
            TabInfo[event.currentTarget.id].callBack(id)
        }
    }
    const handleKeyDown = (e, tabData) => {
        if (props.onKeyDown) {
            props.onKeyDown(e)
        }
        if (e.key === 'Enter' || (e.key === ' ' && !(tabData.disabled !== undefined && tabData.disabled === true))) {
            handleClick(e)
        }
    }

    return (
        <div className="TabWrapper" data-test-id={testId}>
           {enableCarousel ? <div className={cn("Tab__container", {
                "Tab__container--carousel": enableCarousel
            })}>
                {enableCarousel && showPrevButton && (
                    <button
                        className="Tab__carousel-button Tab__carousel-button--prev"
                        onClick={handlePrevious}
                        aria-label="Previous tabs"
                        type="button"
                    >
                        <Icon icon='LeftArrow' className="Tab__carousel-icon" />
                    </button>
                )}
                
                <div 
                    className={cn("Tab__scroll-container", {
                        "Tab__scroll-container--carousel": enableCarousel
                    })}
                    ref={tabsContainerRef}
                >
                    <div 
                        className={cn(classNames, {
                            "Tab--carousel": enableCarousel
                        })}
                        ref={tabsWrapperRef}
                    >
                        {TabInfo.map((tabData, key: any) => {
                            return (
                                <div
                                    data-test-id={tabData?.testId || "cs-tabs-item"}
                                    className={`Tab__item  ${tabData.disabled !== undefined && tabData.disabled === true ? 'Tab__disabled' : (tabData.hasError ? 'Tab__error' : '')
                                        } ${TabItemType} ${key === TabIndex ? 'Tab__selected' : ''}`}
                                    key={key}
                                    onClick={tabData.disabled !== undefined && tabData.disabled === true ? undefined : handleClick}
                                    id={key}
                                    onKeyDown={(e) => handleKeyDown(e, tabData)}
                                    tabIndex={tabData.disabled !== undefined && tabData.disabled === true ? -1 : 0}>
                                    <TabCreator
                                        disabled={tabData.disabled}
                                        tabComponentHeader={tabData.componentTitle}
                                        tabHeader={tabData.title}
                                        id={key}
                                        TabIndex={TabIndex}
                                        type={type}
                                    />
                                </div>
                            )
                        })}
                    </div>
                </div>
                
                {enableCarousel && showNextButton && (
                    <button
                        className="Tab__carousel-button Tab__carousel-button--next"
                        onClick={handleNext}
                        aria-label="Next tabs"
                        type="button"
                    >
                        <Icon icon='RightArrow' className="Tab__carousel-icon" />
                    </button>
                )}
            </div> : (<div className={classNames}>
                {TabInfo.map((tabData, key: any) => {
                    return (
                        <div
                            data-test-id={tabData?.testId || "cs-tabs-item"}
                            className={`Tab__item  ${tabData.disabled !== undefined && tabData.disabled === true ? 'Tab__disabled' : (tabData.hasError ? 'Tab__error' : '')
                                } ${TabItemType} ${key === TabIndex ? 'Tab__selected' : ''}`}
                            key={key}
                            onClick={tabData.disabled !== undefined && tabData.disabled === true ? undefined : handleClick}
                            id={key}
                            onKeyDown={(e) => handleKeyDown(e, tabData)}
                            tabIndex={tabData.disabled !== undefined && tabData.disabled === true ? -1 : 0}>
                            <TabCreator
                                disabled={tabData.disabled}
                                tabComponentHeader={tabData.componentTitle}
                                tabHeader={tabData.title}
                                id={key}
                                TabIndex={TabIndex}
                                type={type}
                            />
                        </div>
                    )
                })}
            </div>)}
            
            {TabInfo && TabInfo.length > 0 && !!TabInfo[TabIndex] ? (
                <div className="Tab__Info">
                    <TabDataCreator tabData={TabInfo[TabIndex].data} tabComponent={TabInfo[TabIndex].componentData} />
                </div>
            ) : (
                ''
            )}
        </div>
    )
}

export default Tabs